import {
  Box,
  ButtonBase,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Toolbar,
  Stack,
  IconButton,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Security as SecurityIcon,
  Factory as FactoryIcon,
  Notifications as NotificationsIcon,
  NotificationImportant as CriticalAlertsIcon,
  Assessment as AssessmentIcon,
  Settings as SettingsIcon,
  ChevronLeft as ChevronLeftIcon,
  DevicesOther as DevicesOtherIcon,
  Accessibility as ErgoIcon,
  BatteryChargingFull as FatigueIcon,
  SwapHoriz as SwitchIcon,
  Replay as ReplayIcon,
  CalendarMonth as CalendarIcon,
  DoNotDisturbOn as RestrictedZoneIcon,
  Report as IncidentIcon,
  SpaceDashboard as CommandCenterIcon,
  TrendingUp as PredictionsIcon,
  Checklist as TasksIcon,
} from '@mui/icons-material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import type { RootState } from '@/store';
import { selectActiveAlerts } from '@/store/slices/realtimeSlice';
import { selectCurrentUser } from '@/store/slices/authSlice';

interface NavItem {
  label: string;
  icon: React.ReactNode;
  segment: string;
  roles?: string[]; // If set, only these roles see this item
  requireGlobal?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  // F2-5: roles mirror router.tsx allowedRoles so a visible item never lands on Access Denied.
  { label: 'Command Center', icon: <CommandCenterIcon />, segment: 'command-center', roles: ['ADMIN', 'SUPERVISOR', 'SUPER_ADMIN'] },
  { label: 'Live Site View', icon: <DashboardIcon />,    segment: 'dashboard' },
  { label: 'Workers',      icon: <PeopleIcon />,        segment: 'workers' },
  { label: 'Weekly Schedule', icon: <CalendarIcon />,   segment: 'schedule' },
  { label: 'Safety',       icon: <SecurityIcon />,      segment: 'safety',        roles: ['ADMIN', 'SUPERVISOR'] },
  { label: 'Safety Incidents', icon: <IncidentIcon />,  segment: 'safety-incidents', roles: ['ADMIN', 'SUPERVISOR'] },
  { label: 'Restricted Zones', icon: <RestrictedZoneIcon />, segment: 'restricted-zones' },
  { label: 'Ergonomics',   icon: <ErgoIcon />,          segment: 'ergonomics' },
  { label: 'Fatigue',      icon: <FatigueIcon />,       segment: 'fatigue' },
  { label: 'Production',   icon: <FactoryIcon />,       segment: 'production' },
  { label: 'Tasks',        icon: <TasksIcon />,         segment: 'tasks',         roles: ['ADMIN', 'SUPERVISOR'] },
  { label: 'Alerts',       icon: <NotificationsIcon />, segment: 'alerts' },
  { label: 'Critical Alerts', icon: <CriticalAlertsIcon />, segment: 'alerts/console' },
  { label: 'Reports',      icon: <AssessmentIcon />,    segment: 'reports' },
  { label: 'Predictions',  icon: <PredictionsIcon />,   segment: 'predicted-risks', roles: ['ADMIN', 'SUPERVISOR'] },
  { label: 'Replay',       icon: <ReplayIcon />,        segment: 'replay' },
  { label: 'Device Health',       icon: <DevicesOtherIcon />, segment: 'device-health',       roles: ['ADMIN', 'SUPERVISOR'] },
  { label: 'Settings',     icon: <SettingsIcon />,      segment: 'settings',      roles: ['ADMIN', 'SUPERVISOR'] },
  // Demo Control moved to the operator SPA (platform-admin.sentrysolutions.info).
];

interface SidebarProps {
  onClose?: () => void;
  showCloseButton?: boolean;
}

export default function Sidebar({ onClose, showCloseButton }: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { facilityId } = useParams<{ facilityId: string }>();
  const alerts = useSelector(selectActiveAlerts);
  const currentFacility = useSelector(
    (state: RootState) => state.facilities.currentFacility,
  );
  const allFacilities = useSelector(
    (state: RootState) => state.facilities.facilities,
  );
  const canSwitchFacility = allFacilities.length > 1;
  const currentUser = useSelector(selectCurrentUser);

  const handleSwitchFacility = () => {
    navigate('/facilities');
    onClose?.();
  };

  const basePath = `/facilities/${facilityId}`;

  const visibleItems = NAV_ITEMS.filter(item => {
    if (item.requireGlobal && currentUser?.is_global !== true) return false;
    if (!item.roles) return true;
    const userRole = (currentUser?.role || '').toUpperCase();
    // SUPER_ADMIN bypasses every role gate (matches the backend convention —
    // see controllers/anchorsController.ts:33, controllers/facilitiesController.ts:56,
    // middleware/tenantContext.ts:28). Anything ADMIN can see, SUPER_ADMIN can too.
    if (userRole === 'SUPER_ADMIN') return true;
    return item.roles.includes(userRole);
  });

  const isSelected = (segment: string) => {
    const itemPath = `${basePath}/${segment}`;
    if (segment === 'alerts') return location.pathname === itemPath;
    if (segment === 'alerts/console') return location.pathname.startsWith(`${basePath}/alerts/`);
    return location.pathname.startsWith(itemPath);
  };

  const handleNav = (segment: string) => {
    navigate(`${basePath}/${segment}`);
    onClose?.();
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
          <Typography variant="h6" noWrap fontWeight={700} sx={{ flexGrow: 1 }}>
            Sentry Solutions
          </Typography>
          {showCloseButton && (
            <IconButton size="small" onClick={onClose} aria-label="Close navigation sidebar">
              <ChevronLeftIcon />
            </IconButton>
          )}
        </Stack>
      </Toolbar>

      {currentFacility && (
        <Box px={2} pb={1}>
          {canSwitchFacility ? (
            <Tooltip title="Switch facility" placement="right">
              <ButtonBase
                onClick={handleSwitchFacility}
                aria-label={`Current facility ${currentFacility.name}. Click to switch.`}
                sx={{
                  width: '100%',
                  justifyContent: 'flex-start',
                  borderRadius: 1,
                  p: 0.5,
                  '&:hover': { bgcolor: 'action.hover' },
                  '&:focus-visible': { outline: (t) => `2px solid ${t.palette.primary.main}` },
                }}
              >
                <Chip
                  label={currentFacility.name}
                  size="small"
                  color="primary"
                  variant="outlined"
                  icon={<SwitchIcon />}
                  clickable
                  sx={{ maxWidth: '100%', fontWeight: 500, pointerEvents: 'none' }}
                />
              </ButtonBase>
            </Tooltip>
          ) : (
            <Chip
              label={currentFacility.name}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ maxWidth: '100%', fontWeight: 500 }}
            />
          )}
        </Box>
      )}

      <Divider />

      <List
        component="nav"
        aria-label="Main navigation"
        sx={{ flexGrow: 1, pt: 1 }}
      >
        {visibleItems.map((item) => {
          const active = isSelected(item.segment);
          const showBadge =
            item.segment === 'alerts' && alerts.length > 0;

          return (
            <ListItemButton
              key={item.segment}
              data-tour={`nav-${item.segment.replace(/\//g, '-')}`}
              selected={active}
              onClick={() => handleNav(item.segment)}
              aria-current={active ? 'page' : undefined}
              sx={{
                borderRadius: 1,
                mx: 1,
                mb: 0.5,
                '&.Mui-disabled': {
                  opacity: 1,
                  color: 'text.disabled',
                  '& .MuiListItemIcon-root': { color: 'text.disabled' },
                  '& [data-nav-alert-badge="true"]': { bgcolor: 'action.disabled' },
                },
                '&.Mui-selected': {
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  '& .MuiListItemIcon-root': { color: 'primary.contrastText' },
                  '&:hover': { bgcolor: 'primary.dark' },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                {showBadge ? (
                  <Box position="relative" display="inline-flex">
                    {item.icon}
                    <Box
                      data-nav-alert-badge="true"
                      aria-label={`${alerts.length} active alert${alerts.length !== 1 ? 's' : ''}`}
                      role="status"
                      sx={{
                        position: 'absolute',
                        top: -4,
                        right: -4,
                        bgcolor: 'error.main',
                        borderRadius: '50%',
                        width: 16,
                        height: 16,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography
                        aria-hidden="true"
                        variant="caption"
                        sx={{ fontSize: 10, color: 'white', lineHeight: 1 }}
                      >
                        {alerts.length > 9 ? '9+' : alerts.length}
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  item.icon
                )}
              </ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          );
        })}
      </List>

      <Divider />
      <Box px={2} py={1.5}>
        <Typography variant="caption" color="text.secondary">
          Sentry Solutions v2
        </Typography>
      </Box>
    </Box>
  );
}
